import React, { useState, useEffect } from 'react';
import { Modal, Input, Form } from 'antd';
import { getAlert, insertData, getDataWithComp } from '../../../utils/SamuiUtils';

export default function AddCategoryUnitModal({
  open,
  onClose,
  onSuccess,
  existingGroups = [],
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  const validateGroupCodeName = (_, value) => {
    if (!value) return Promise.reject(new Error('กรุณากรอกรหัสหมวดหมู่'));

    const isDuplicate = existingGroups.some(
      (g) =>
        String(g.code || '').toLowerCase() ===
        String(value || '').toLowerCase()
    );
    if (isDuplicate) {
      return Promise.reject(new Error('รหัสหมวดหมู่นี้มีอยู่ในระบบแล้ว'));
    }

    return Promise.resolve();
  };

  const validateGroupName = (_, value) => {
    if (!value) return Promise.reject(new Error('กรุณากรอกชื่อหมวดหมู่'));

    const isDuplicate = existingGroups.some(
      (g) =>
        String(g.name || '').toLowerCase() ===
        String(value || '').toLowerCase()
    );
    if (isDuplicate) {
      return Promise.reject(new Error('ชื่อหมวดหมู่นี้มีอยู่ในระบบแล้ว'));
    }

    return Promise.resolve();
  };

  const handleSubmitCategory = async () => {
    try {
      await form.validateFields(['Group_Code', 'Group_Name']);
      const values = form.getFieldsValue();
      setLoading(true);

      const compId = localStorage.getItem('company');

      if (!compId) {
        getAlert('FAILED', 'ไม่พบ Company ID');
        setLoading(false);
        return;
      }

      // ✅ ตรวจสอบซ้ำกับ DB
      let codeCheck, nameCheck;
      try {
        codeCheck = await getDataWithComp(
          'Tb_Set_ItmGroup',
          `AND Group_Code='${values.Group_Code.trim()}' AND Group_Status='Y'`
        );
      } catch (e) {
        console.error('❌ Error checking Group_Code:', e);
        getAlert('FAILED', 'ไม่สามารถตรวจสอบข้อมูลซ้ำได้ กรุณาลองใหม่อีกครั้ง');
        setLoading(false);
        return;
      }

      if (codeCheck && codeCheck.length > 0) {
        getAlert('FAILED', `รหัสหมวดหมู่ "${values.Group_Code}" มีอยู่ในระบบแล้ว`);
        setLoading(false);
        return;
      }

      try {
        nameCheck = await getDataWithComp(
          'Tb_Set_ItmGroup',
          `AND Group_Name='${values.Group_Name.trim()}' AND Group_Status='Y'`
        );
      } catch (e) {
        console.error('❌ Error checking Group_Name:', e);
        getAlert('FAILED', 'ไม่สามารถตรวจสอบข้อมูลซ้ำได้ กรุณาลองใหม่อีกครั้ง');
        setLoading(false);
        return;
      }

      const fields = 'Group_Code,Group_Name,Group_Status,Comp_Id,Doc_Type';
      const valuesArray = [[
        values.Group_Code.trim(),
        values.Group_Name.trim(),
        'Y',
        Number(compId),
        1, // Doc_Type: 1 = สินค้า
      ]];

      console.debug('🔍 Calling insertData with:', {
        table: 'Tb_Set_ItmGroup',
        fields,
        values: valuesArray,
        compId: Number(compId),
        unitId: null,
      });

      const response = await insertData('Tb_Set_ItmGroup', fields, valuesArray);

      console.debug('📦 Response from insertData:', response);
      console.debug('📊 Response type:', typeof response);
      console.debug('✓ Has ok property:', 'ok' in (response || {}));
      console.debug('✓ response.ok value:', response?.ok);

      if (response && response.ok !== false) {
        // insertData คืนค่า {ok: true, data: [insertId]} หรือ null ถ้า error
        let newGroupId = response.data?.[0] || response.insertId;
        console.debug('✅ Insert successful! newGroupId:', newGroupId);

        if (!newGroupId) {
          try {
            const rows = await getDataWithComp(
              'Tb_Set_ItmGroup',
              `AND Group_Code='${values.Group_Code.trim()}' AND Group_Status='Y'`
            );
            const row = Array.isArray(rows) ? rows[0] : rows?.data?.[0];
            newGroupId = row?.Group_Id ?? row?.id ?? row?.ID;
          } catch (e) {
            console.error('❌ Error fetching new group id:', e);
          }
        }
        
        // ✅ รอให้ onSuccess ทำงานเสร็จก่อนปิด modal
        await onSuccess?.('category', {
          id: newGroupId,
          code: values.Group_Code.trim(),
          name: values.Group_Name.trim(),
        });
        
        getAlert('OK', 'เพิ่มหมวดหมู่สำเร็จ');
        onClose();
      } else {
        console.error('❌ Insert group failed:', response);
        const errorMsg = response?.error || response?.message || 'ไม่สามารถเพิ่มหมวดหมู่ได้';
        getAlert('FAILED', `เกิดข้อผิดพลาด: ${errorMsg}`);
      }
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      console.error('❌ Error adding category:', error);
      const errorMsg = error?.response?.data?.error || error?.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อกับเซิร์ฟเวอร์';
      getAlert('FAILED', `เกิดข้อผิดพลาด: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOk = () => {
    handleSubmitCategory();
  };

  return (
    <Modal
      open={open}
      title="เพิ่มหมวดหมู่ใหม่"
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={loading}
      okText="บันทึก"
      cancelText="ยกเลิก"
      width={500}
      destroyOnClose
      zIndex={10600}
    >
      <div style={{ marginTop: 16 }}>
        <Form form={form} layout="vertical">
          <>
            <Form.Item
              name="Group_Code"
              label="รหัสหมวดหมู่"
              rules={[{ validator: validateGroupCodeName }]}
              extra={
                <span style={{ fontSize: '12px', color: '#f97316' }}>
                  ⚠️ ใช้สำหรับสร้างรหัสสินค้า
                </span>
              }
            >
              <Input
                placeholder="เช่น SH, TS, PN"
                maxLength={10}
                size="large"
                style={{ height: "35px" }}
              />
            </Form.Item>

            <Form.Item
              name="Group_Name"
              label="ชื่อหมวดหมู่"
              rules={[{ validator: validateGroupName }]}
            >
              <Input
                placeholder="เช่น สารละลาย, กรด, ด่าง, ตัวทำละลาย"
                maxLength={50}
                size="large"
                style={{ height: "35px" }}
              />
            </Form.Item>

          </>
        </Form>
      </div>
    </Modal>
  );
}
